import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import ToolTip from "components/design/toolTip";
import { Undo } from "components/icons/Undo";
import { UserTrustIcon } from "components/icons/JouneyTooltip/UserTrustIcon";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import BankCircular from "static/images/BankCircular.svg";
import {
  useGetEntityBanksQuery,
  useUpdateBankAccountantAccessMutation,
} from "store/apis/bankConnections";
import { BANK_ACCESS_BLOG_URL } from "constants/base";
import { BANK_ACCESS_HELP } from "constants/task";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { GrantAccountantAccessModal } from "components/OnboardingWidget/Tax/GrantAccountantAccessModal";
import classNames from "classnames";

type BankGroup = {
  bankName: string;
  logo?: string;
  accounts: {
    uuid: string;
    nickname: string;
    mask: string;
  }[];
  entityItems: {
    uuid: string;
    accountantAccess: boolean;
  }[];
};

const BankAccessCell = ({
  bank,
  entityId,
}: {
  bank: BankGroup;
  entityId: string;
}) => {
  const [updateBankAccountantAccess, { isLoading }] =
    useUpdateBankAccountantAccessMutation();
  const { alertToast, successToast } = useToast();
  const grantAccessModal = useModal();

  const isAccessGranted =
    bank.entityItems.length > 0 &&
    bank.entityItems.every((item) => item.accountantAccess);

  const updateAccessForAllEntityItems = async (accountantAccess: boolean) => {
    try {
      for (const entityItem of bank.entityItems) {
        await updateBankAccountantAccess({
          entityId,
          entityItemId: entityItem.uuid,
          accountantAccess,
        }).unwrap();
      }
      successToast({ message: "Access updated successfully" });
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const handleConfirmAccessGranted = async () => {
    await updateAccessForAllEntityItems(true);
    grantAccessModal.close();
  };

  const handleUndoAccessGranted = () => updateAccessForAllEntityItems(false);

  if (isAccessGranted) {
    return (
      <div className="t-flex t-items-center t-justify-end t-gap-2 t-w-full">
        <Tag tagType="green" icon={false}>
          Access granted
        </Tag>
        <ToolTip text="Undo">
          <span>
            <Button
              customType="ghost_icon"
              size="small"
              type="button"
              onClick={handleUndoAccessGranted}
              disabled={isLoading}
            >
              <Undo />
            </Button>
          </span>
        </ToolTip>
      </div>
    );
  }

  return (
    <div className="t-flex t-items-center t-justify-end t-gap-2 t-w-full">
      <Button
        customType="primary_outlined"
        size="small"
        type="button"
        onClick={grantAccessModal.open}
      >
        Grant access
      </Button>
      <ToolTip text="Mark as access granted already">
        <span>
          <Button
            customType="icon"
            size="small"
            type="button"
            onClick={handleConfirmAccessGranted}
            disabled={isLoading}
          >
            <UserTrustIcon />
          </Button>
        </span>
      </ToolTip>
      <GrantAccountantAccessModal
        open={grantAccessModal.isOpen}
        bankName={bank.bankName}
        isLoading={isLoading}
        onCancel={grantAccessModal.close}
        onConfirm={handleConfirmAccessGranted}
      />
    </div>
  );
};

const BankAccessRow = ({
  bank,
  entityId,
  isLast,
}: {
  bank: BankGroup;
  entityId: string;
  isLast: boolean;
}) => {
  const accountCount = bank.accounts.length;

  return (
    <div
      className={classNames(
        "t-flex t-items-center t-justify-between t-py-2.5 sm:t-gap-2",
        {
          "t-border-0 t-border-b t-border-solid t-border-neutral-0": !isLast,
        }
      )}
    >
      <div className="t-flex t-items-start t-gap-3 sm:t-min-w-0 sm:t-gap-2">
        <img
          src={bank.logo || BankCircular}
          className="t-w-6 t-h-6 t-shrink-0 t-rounded-full"
          alt={bank.bankName}
        />
        <div className="t-flex t-flex-col sm:t-min-w-0">
          <span className="t-text-body-sm t-whitespace-nowrap sm:t-whitespace-normal">
            {bank.bankName}
          </span>
          <span className="t-text-body-sm t-text-text-30 t-whitespace-nowrap">
            {pluralize(accountCount, "account", "accounts")}
          </span>
        </div>
      </div>
      <BankAccessCell bank={bank} entityId={entityId} />
    </div>
  );
};

const ShareAccountantAccessList = ({
  banks,
  entityId,
}: {
  banks: BankGroup[];
  entityId: string;
}) => {
  return (
    <div className="t-flex t-flex-col t-w-full">
      {banks.map((bank, index) => (
        <BankAccessRow
          key={bank.bankName}
          bank={bank}
          entityId={entityId}
          isLast={index === banks.length - 1}
        />
      ))}
    </div>
  );
};

export const ShareBankAccountPreferencesContent = ({
  entityId,
  groupId,
}: {
  entityId: string;
  groupId: string;
}) => {
  const { data: ledger } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );
  const { accounts = [], items = [] } = ledger || {};

  const connectedBanks: BankGroup[] = Object.values(
    accounts.reduce<Record<string, BankGroup>>((acc, item) => {
      const bankName = item.bank_brand.name;

      if (!acc[bankName]) {
        acc[bankName] = {
          bankName,
          logo: item.bank_brand.logo,
          accounts: [],
          entityItems: items
            .filter((i) => i.bank_brand.name === bankName)
            .map((i) => ({
              uuid: i.uuid,
              accountantAccess: i.accountant_access,
            })),
        };
      }

      acc[bankName].accounts.push({
        uuid: item.account.uuid,
        nickname: item.account.nickname,
        mask: item.account.mask,
      });

      return acc;
    }, {})
  );

  return (
    <div className="t-flex t-flex-col t-w-full t-gap-3">
      <div className="t-text-body-sm t-text-text-30">
        Providing accountant access allows our bookkeeping team to securely
        retrieve full bank statements for reconciliation, ensure accurate
        bookkeeping, and assist you with any accounting queries or requirements.
      </div>
      <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-5 t-flex t-flex-col t-gap-3 sm:t-p-4">
        <span className="t-text-subtext t-text-text-100">
          Grant accountant access
        </span>
        <div className="t-text-body-sm t-text-text-30">
          To share access, Log in to your bank → Add Inkle as Accountant →
          Confirm. <br />
          <ConditionalLink to={BANK_ACCESS_HELP}>
            <a
              href={BANK_ACCESS_BLOG_URL}
              target="_blank"
              rel="noreferrer"
              className="t-text-purple t-no-underline"
            >
              View instructions
            </a>
          </ConditionalLink>{" "}
          for each bank.
        </div>
        {connectedBanks.length > 0 && (
          <ShareAccountantAccessList
            banks={connectedBanks}
            entityId={entityId}
          />
        )}
      </div>
    </div>
  );
};

export const ShareBankAccountPreferences = () => {
  const group = useCurrentGroupContext();
  const groupId = group?.uuid || "";
  const { entityId: entityIdOnSelect } = useAppSelector(
    (state) => state.onboardingWidget
  );
  const entityId = useCurrentEntityId() || entityIdOnSelect!;

  return (
    <div className="t-max-w-[652px] t-flex t-flex-col t-items-center t-gap-6 t-mx-auto t-p-3">
      <div className="t-flex t-flex-col t-w-full t-gap-3">
        <span className="t-text-subtext">Share accountant access</span>
        <ShareBankAccountPreferencesContent
          entityId={entityId}
          groupId={groupId}
        />
      </div>
    </div>
  );
};
